import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import {
	setSelectedAgent,
	setSelectedAgentId,
	setCurrentPage,
	setAgents,
} from '@/store/account/slice';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import {
	getAllAgents,
	getInboundAgents,
	getLoggedInUser,
	getOutboundAgents,
	getSelectedAgent,
	getSelectedAgentId,
} from '@/store/account/selector';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'src/enums/ERoutes';
import { User } from '@/types/TAccounts';
import { useQuery } from '@tanstack/react-query';
import { getAgents } from '@/api/user';
import { useEffect } from 'react';

export const useAgents = () => {
	const loggedInUser = useAppSelector(getLoggedInUser);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	// Selectors
	const allAgents = useAppSelector(getAllAgents);
	const inboundAgents = useAppSelector(getInboundAgents);
	const outboundAgents = useAppSelector(getOutboundAgents);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	// Fetch agents data
	const {
		data: agentsData,
		refetch: refetchAgents,
		isPending: isAgentsLoading,
	} = useQuery({
		queryKey: ['get-agents'],
		queryFn: () => getAgents({}),
		enabled: !!loggedInUser.username,
	});

	// Update agents in store when data is fetched
	useEffect(() => {
		if (agentsData?.success && agentsData?.data) {
			dispatch(setAgents({ agents: agentsData?.data }));
		}
	}, [agentsData, dispatch]);

	const openInboundAgent = (agentNumber: string) => {
		const agent = inboundAgents.find((agent: User) => agent.number === agentNumber);
		if (agent) {
			// First navigate to ensure route is ready
			navigate(AppRoutes.INBOUND_AGENT_PORTAL.replace(':agentId', agentNumber));

			// Then update state
			dispatch(setSelectedAgentId({ selectedAgentId: agentNumber }));
			dispatch(setSelectedAgent({ user: agent }));
			dispatch(setCurrentPage(ReceptionistDashboardSiderItem.RECEPTIONIST));
		}
	};

	const openOutboundAgent = (agentNumber: string) => {
		const agent = outboundAgents.find((agent: User) => agent.number === agentNumber);
		if (agent) {
			navigate(AppRoutes.OUTBOUND_AGENT_PORTAL.replace(':agentId', agentNumber));
			dispatch(setSelectedAgentId({ selectedAgentId: agentNumber }));
			dispatch(setSelectedAgent({ user: agent }));
		}
	};

	const clearSelectedAgent = () => {
		dispatch(setSelectedAgentId({ selectedAgentId: null }));
		dispatch(setSelectedAgent({ user: null }));
	};

	const goToDashboard = () => {
		navigate(AppRoutes.DASHBOARD);
		clearSelectedAgent();
		dispatch(setCurrentPage(ReceptionistDashboardSiderItem.DASHBOARD));
	};

	return {
		// Data
		inboundAgents,
		outboundAgents,
		selectedAgent,
		selectedAgentId,
		isAgentsLoading,
		allAgents,

		// Actions
		openInboundAgent,
		openOutboundAgent,
		clearSelectedAgent,
		goToDashboard,
		refetchAgents,
	};
};
